import React, { useEffect, useState } from "react";
import {
	Container,
	Row,
	Col,
	Dropdown,
	Card,
	Table,
	Button,
} from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { FaCalendar } from "react-icons/fa";
import { Helmet } from "react-helmet";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import { getEventsThunk } from "../../store/thunks/fantasyThunk";
import ResultsModal from "./components/ResultModal";
import { getResults } from "../../services/resultService";
import { Event, initialSeason, Season, Series, Sport } from "../../types/model";
import "./schedulePage.css";

const SchedulePage = () => {
	const dispatch = useAppDispatch();
	const {
		events,
		series,
		sports,
		seasons,
		selectedSeason,
		selectedSeries,
		selectedSport,
		selectedEvent,
	} = useAppSelector((state: RootState) => state.fantasy);

	const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
	const [showEventResults, setShowEventResults] = useState(false);
	const [eventResults, setEventResults] = useState([]);
	const [chosenSeason, setChosenSeason] = useState<Season>(selectedSeason);
	const [chosenSport, setChosenSport] = useState<Sport>(selectedSport);
	const [chosenSeries, setChosenSeries] = useState<Series>(selectedSeries);

	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (selectedSeason?._id) {
			setChosenSeason(selectedSeason);
		} else {
			const season = seasons.find((season: Season) => season.isActive === true);
			if (!season) {
				return;
			}
			setChosenSeason(season);
		}

		if (selectedSport?._id) {
			setChosenSport(selectedSport);
		} else {
			const sport = sports.find(
				(sport: Sport) => sport._id === selectedEvent.sport._id,
			);
			if (!sport) {
				return;
			}
			setChosenSport(sport);
		}

		if (selectedSeries?._id) {
			setChosenSeries(selectedSeries);
		} else {
			const _series = series.find(
				(series: Series) => series._id === selectedEvent.series._id,
			);
			if (!_series) {
				return;
			}
			setChosenSeries(_series);
		}
	}, [selectedSeason, selectedSport, selectedSeries]);

	useEffect(() => {
		if (events.length === 0) {
			dispatch(getEventsThunk());
		}
	}, [dispatch, events.length]);

	const isEventPast = (date: Date) => {
		const today = new Date();
		const eventDate = new Date(date);
		return eventDate < today;
	};

	const fetchResults = async (eventId: string) => {
		try {
			const res = await getResults(eventId);
			if (res) {
				setEventResults(res);
			}
		} catch (error) {
			console.error("Error fetching results:", error);
		}
	};

	useEffect(() => {
		// Filter events whenever filter criteria or events list changes
		const filtered = events.filter((event: Event) => {
			const matchesSeason = chosenSeason
				? event.season === chosenSeason._id
				: true;
			const matchesSport = chosenSport
				? event.sport._id === chosenSport._id
				: true;
			const matchesSeries = chosenSeries
				? event.series._id === chosenSeries._id
				: true;

			return matchesSeason && matchesSport && matchesSeries;
		});

		setFilteredEvents(filtered);
	}, [events, chosenSeason, chosenSport, chosenSeries]);

	const handleSelectViewResults = (eventId: string) => {
		setShowEventResults(true);
		fetchResults(eventId);
	};

	const handleSeasonChange = (season: Season) => {
		setChosenSeason(season);
	};

	const handleSportChange = (sport: Sport) => {
		setChosenSport(sport);
	};

	const handleSeriesChange = (series: Series) => {
		setChosenSeries(series);
	};

	return (
		<Container className="schedule-page w-100 text-center">
			<Helmet>
				<title>Schedule - The Race Companion</title>
				<meta
					name="description"
					content="Schedule for Enduro, Slopestyle, Downhill and Freeride mountain biking events. Follow along with Crankworx, World cups, Red Bull Hardline and Red Bull Rampage."
				/>
				<meta name="viewport" content="width=device-width, initial-scale=1" />
			</Helmet>
			<h1 className="schedule-page__title ms-md-0 ms-2 text-start my-4">
				<FaCalendar className="mb-2 me-1" /> RACE SCHEDULE
			</h1>

			<Card className="schedule-page__card border-secondary">
				<Card.Body style={{ overflow: "auto" }}>
					<Row className="mb-3">
						<Col md={4} className="mb-md-0 mb-2">
							<Dropdown>
								<Dropdown.Toggle
									className="schedule-page__filter-toggle w-100"
									variant="outline-light"
									id="season-dropdown"
								>
									{chosenSeason ? chosenSeason.name : "Season"}
								</Dropdown.Toggle>
								<Dropdown.Menu className="schedule-page__filter-menu">
									{seasons &&
										seasons.map((season: Season) => (
											<Dropdown.Item
												key={season._id}
												onClick={() => handleSeasonChange(season)}
												className="schedule-page__filter-item"
											>
												{season.name}
											</Dropdown.Item>
										))}
								</Dropdown.Menu>
							</Dropdown>
						</Col>
						<Col md={4} className="mb-md-0 mb-2">
							<Dropdown>
								<Dropdown.Toggle
									className="schedule-page__filter-toggle w-100"
									variant="outline-light"
									id="sport-dropdown"
								>
									{chosenSport ? chosenSport.name : "Discipline"}
								</Dropdown.Toggle>
								<Dropdown.Menu className="schedule-page__filter-menu">
									{sports &&
										sports.map((sport) => (
											<Dropdown.Item
												key={sport._id}
												onClick={() => handleSportChange(sport)}
												className="schedule-page__filter-item"
											>
												{sport.name}
											</Dropdown.Item>
										))}
								</Dropdown.Menu>
							</Dropdown>
						</Col>
						<Col md={4} className="mb-md-0 mb-2">
							<Dropdown>
								<Dropdown.Toggle
									className="schedule-page__filter-toggle w-100"
									variant="outline-light"
									id="series-dropdown"
								>
									{chosenSeries ? chosenSeries.name : "Series"}
								</Dropdown.Toggle>
								<Dropdown.Menu className="schedule-page__filter-menu">
									{series &&
										series.map((s) => (
											<Dropdown.Item
												key={s._id}
												onClick={() => handleSeriesChange(s)}
												className="schedule-page__filter-item"
											>
												{s.name}
											</Dropdown.Item>
										))}
								</Dropdown.Menu>
							</Dropdown>
						</Col>
					</Row>
					<Table
						striped
						hover
						className="schedule-page__table"
					>
						<thead>
							<tr>
								<th>Event</th>
								<th>Series</th>
								<th>Discipline</th>
								<th>Date</th>
								<th>Location</th>
								<th>Organizer Page</th>
								<th>Results</th>
							</tr>
						</thead>
						<tbody>
							{filteredEvents.map((event) => (
								<tr key={event._id}>
									<td>{event.name}</td>
									<td>{event.series ? event.series.name : "N/A"}</td>
									<td>{event.sport.name}</td>
									<td>{new Date(event.date).toLocaleDateString()}</td>
									<td className="text-start">
										<ReactCountryFlag
											countryCode={event.country}
											svg
											style={{ height: "1.5rem", width: "1.5rem" }}
											className="me-2"
										/>
										{event.location}
									</td>
									<td>
										<Button
											variant="info"
											size="sm"
											className="schedule-page__action-btn"
											onClick={() => window.open(event.eventUrl, "_blank")}
										>
											Organizer Page
										</Button>
									</td>
									<td>
										<Button
											variant="primary"
											size="sm"
											className="schedule-page__results-btn"
											onClick={() => handleSelectViewResults(event?._id || "")}
										>
											View Results
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
			<ResultsModal
				showEventResults={showEventResults}
				eventResults={eventResults}
				closeModal={() => setShowEventResults(false)}
			/>
		</Container>
	);
};

export default SchedulePage;
